import { useState, type ReactNode } from "react";
import { PipelineCell, ColHeader } from "./PipelineCell.js";
import { Skeleton } from "./Skeleton.js";
import { AsyncButton } from "./ui/async-button.js";
import { Button } from "./ui/button.js";
import type { WeeklyHistoryDay, WeeklyHoursHistoryRow } from "../pages/AttendancePage.js";
import { approverRoleLabel, weeklyStatusDisplay } from "../pages/AttendancePage.js";
import { isoWeekLabel } from "../lib/isoWeek.js";
import type { HistoryPeriod } from "../lib/historyRange.js";
import { SpecularButton } from "./ui/specular-button.js";
import { StatusChip } from "./ui/status-chip.js";
import { Badge } from "./ui/badge.js";
import { cn } from "../lib/utils.js";
import { ProgressRing } from "./ui/progress-ring.js";
import { TONE_BY_STATUS, type CellStatus } from "./attendance/attendanceCells.js";
import { ChevronDown } from "lucide-react";

const COLS = { week: 140, hours: 152, status: 165, approvedBy: 130, remark: 180 };

/** A week the list shows without attendance behind it. */
type EmptyWeekState = "upcoming" | "inProgress" | "notAvailable";

interface WeekEntry {
  weekStart: string;
  weekEnd: string;
  /** The week's record, or null when there is no attendance for it. */
  record: WeeklyHoursHistoryRow | null;
}

const toDayKey = (d: Date) => d.toISOString().slice(0, 10);

/**
 * Every Monday-to-Sunday week that touches [from, to], newest first. A week with no record
 * still gets an entry, so the list reads as the whole month rather than only the weeks that
 * happen to have data.
 */
function weekEntries(history: WeeklyHoursHistoryRow[], from: string, to: string): WeekEntry[] {
  const byWeek = new Map(history.map((r) => [r.weekStart, r]));
  const cur = new Date(from + "T00:00:00Z");
  cur.setUTCDate(cur.getUTCDate() - ((cur.getUTCDay() + 6) % 7)); // back to that week's Monday
  const entries: WeekEntry[] = [];
  while (toDayKey(cur) <= to) {
    const weekStart = toDayKey(cur);
    const weekEnd = new Date(cur);
    weekEnd.setUTCDate(weekEnd.getUTCDate() + 6);
    entries.unshift({ weekStart, weekEnd: toDayKey(weekEnd), record: byWeek.get(weekStart) ?? null });
    cur.setUTCDate(cur.getUTCDate() + 7);
  }
  return entries;
}

/** Upcoming if the week has not started, in progress while it runs, otherwise it passed with no data. */
function emptyWeekState(weekStart: string, weekEnd: string, todayKey: string): EmptyWeekState {
  if (weekStart > todayKey) return "upcoming";
  if (weekEnd >= todayKey) return "inProgress";
  return "notAvailable";
}

const EMPTY_WEEK_BADGE: Record<EmptyWeekState, { label: string; tone: "outline" | "info" | "neutral" }> = {
  upcoming: { label: "Upcoming", tone: "outline" },
  inProgress: { label: "In progress", tone: "info" },
  notAvailable: { label: "Not available", tone: "neutral" },
};

/** A week with no attendance: the same row shape as a real week, with dashes where the data would be. */
function EmptyWeekRow({ weekStart, state, selected, onSelect }: { weekStart: string; state: EmptyWeekState; selected: boolean; onSelect: () => void }) {
  const badge = EMPTY_WEEK_BADGE[state];
  return (
    <div
      onClick={onSelect}
      className={cn(
        "flex cursor-pointer overflow-hidden rounded-lg border bg-surface-0",
        selected ? "border-brand-300 bg-brand-500/10" : "border-surface-200",
      )}
    >
      <div className="w-1 shrink-0 bg-surface-200" />
      <div className="flex min-h-[52px] min-w-0 flex-1 items-center p-2">
        <PipelineCell width={COLS.week} divider={false}>
          {/* The width of the chevron a real week carries, so the labels line up. */}
          <span className="flex items-center gap-1 whitespace-nowrap text-sm font-semibold text-surface-700">
            <span aria-hidden="true" className="inline-block w-3.5 shrink-0" />
            {isoWeekLabel(weekStart)}
          </span>
        </PipelineCell>
        <PipelineCell width={COLS.hours} align="center"><span className="text-surface-700">—</span></PipelineCell>
        <PipelineCell width={COLS.status}><Badge tone={badge.tone}>{badge.label}</Badge></PipelineCell>
        <PipelineCell width={COLS.approvedBy}><span className="text-surface-700">—</span></PipelineCell>
        <PipelineCell width={COLS.remark} grow><span className="text-surface-700">—</span></PipelineCell>
      </div>
    </div>
  );
}

/** A day's status, from the figures the week row already carries. Reuses the shared
 *  tone map so Weekly Hours, My History's Register and All Team read the same. */
function dayStatus(d: WeeklyHistoryDay): CellStatus {
  if (d.isHoliday) return "HOLIDAY";
  if (d.onLeave) return "LEAVE";
  if (d.open || d.actualHours > 0) return "PRESENT";
  if (d.expectedHours > 0) return "ABSENT";
  return "OFF_DAY";
}

const DAY_NAMES = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];

const STATUS_LETTER: Partial<Record<CellStatus, string>> = {
  PRESENT: "P", ABSENT: "A", LEAVE: "L", HOLIDAY: "H",
};

/**
 * The week's seven days, as tiles that sit *inside* the row in place of Approved By
 * and Remark. Opening a week must not change the row's height, so the whole tile is
 * capped at DAY_TILE_H -- the height of the hours ring, which is the tallest thing
 * in an unopened row and therefore what already sets the row's height.
 */
const DAY_TILE_H = 28;

function WeekDayStrip({ days }: { days: WeeklyHistoryDay[] }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 4, width: "100%", minWidth: 0, height: DAY_TILE_H }}>
      {days.map((d) => {
        const status = dayStatus(d);
        const date = new Date(d.date + "T00:00:00Z");
        const worked = d.open || d.actualHours > 0;
        return (
          <div
            key={d.date}
            title={`${date.toUTCString().slice(0, 16)} · ${d.open ? "checked in" : `${d.actualHours.toFixed(1)}h`} of ${d.expectedHours.toFixed(1)}h`}
            style={{
              display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
              flex: "1 1 0", minWidth: 0, height: "100%", padding: "0 3px", boxSizing: "border-box",
              borderRadius: "var(--radius-md)",
              border: `1px solid ${d.open ? "var(--color-warning-accent)" : "var(--color-surface-200)"}`,
              background: worked ? "var(--color-surface-50)" : "transparent",
              overflow: "hidden",
            }}
          >
            <div style={{ fontSize: 9, lineHeight: 1, fontWeight: 700, letterSpacing: "0.3px", color: "var(--color-surface-700)", whiteSpace: "nowrap" }}>
              {DAY_NAMES[date.getUTCDay()]} {date.getUTCDate()}
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 3, minWidth: 0, marginTop: 2 }}>
              {STATUS_LETTER[status] && (
                <StatusChip tone={TONE_BY_STATUS[status]} className="h-3 w-3 text-[8px]">
                  {STATUS_LETTER[status]}
                </StatusChip>
              )}
              <span style={{ fontSize: 9, lineHeight: 1, fontWeight: 600, whiteSpace: "nowrap", color: "var(--color-surface-700)" }}>
                {d.open ? "now" : d.actualHours > 0 ? `${d.actualHours.toFixed(1)}h` : "—"}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

interface Props {
  loading: boolean;
  history: WeeklyHoursHistoryRow[];
  hasAnyRecords: boolean;
  selectedWeekStart: string;
  onSelectWeek: (weekStart: string) => void;
  weekNav?: ReactNode;
  rangeLabel: string;
  /** First and last day of the range, YYYY-MM-DD. Every week that touches it is listed. */
  rangeFrom: string;
  rangeTo: string;
  onPrev: () => void;
  onNext: () => void;
  /**
   * The correction request form. Omit it to render the weeks read-only, which is
   * how the page drops corrections when the org-wide approval switch is off.
   * Grouped rather than left as eleven separate optional props so the component
   * can never be handed half a form. Mirrors AttendanceHistoryTimeline's
   * `correctionEditor`.
   */
  correctionEditor?: WeeklyCorrectionEditor | undefined;
}

export interface WeeklyCorrectionEditor {
  /** The week whose editor is open, or null. */
  requestFor: string | null;
  onRequest: (weekStart: string) => void;
  onEdit: (row: WeeklyHoursHistoryRow) => void;
  hours: string;
  onHoursChange: (v: string) => void;
  reason: string;
  onReasonChange: (v: string) => void;
  busy: boolean;
  err: string;
  onSubmit: (row: WeeklyHoursHistoryRow) => Promise<void>;
  onCancel: () => void;
}

export function WeeklyHoursTimeline({
  loading, history, hasAnyRecords, selectedWeekStart, onSelectWeek, weekNav,
  rangeLabel, rangeFrom, rangeTo, onPrev, onNext,
  correctionEditor,
}: Props) {
  // Which week row is open, showing its seven days.
  const [expandedWeek, setExpandedWeek] = useState<string | null>(null);
  // Today in IST, as a YYYY-MM-DD key: a week is only correctable once it has ended.
  const todayKey = new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" });
  const entries = weekEntries(history, rangeFrom, rangeTo);

  return (
    <div style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: "var(--space-3)", flexWrap: "wrap", marginBottom: "var(--space-2)", flexShrink: 0 }}>
        {weekNav}
        <div style={{ display: "flex", alignItems: "center", gap: "var(--space-3)" }}>
          <SpecularButton onClick={onPrev} aria-label="Previous period" className="w-8 px-0">&larr;</SpecularButton>
          <div style={{ fontWeight: 600, fontSize: 14, minWidth: 140, textAlign: "center" }}>{rangeLabel}</div>
          <SpecularButton onClick={onNext} aria-label="Next period" className="w-8 px-0">&rarr;</SpecularButton>
        </div>
      </div>

      {loading ? (
        // The finished card with its real header and pipeline rows, blocks in place of
        // the values -- same column widths and row height, so nothing shifts on load.
        <div style={{ background: "var(--color-surface-0)", border: "1px solid var(--color-surface-200)", borderRadius: "var(--radius-lg)", padding: "var(--space-4)", display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
          <div style={{ display: "flex", flexWrap: "nowrap", paddingBottom: "var(--space-2)", flexShrink: 0 }}>
            <div style={{ width: 4, flexShrink: 0 }} />
            <div style={{ flex: 1, minWidth: 0, padding: "var(--space-1) var(--space-2)" }}>
              <div style={{ display: "flex", flexWrap: "nowrap" }}>
                <PipelineCell width={COLS.week} divider={false}><ColHeader>Week</ColHeader></PipelineCell>
                <PipelineCell width={COLS.hours} align="center"><ColHeader>Hours</ColHeader></PipelineCell>
                <PipelineCell width={COLS.status}><ColHeader>Status</ColHeader></PipelineCell>
                <PipelineCell width={COLS.approvedBy}><ColHeader>Approved By</ColHeader></PipelineCell>
                <PipelineCell width={COLS.remark} grow><ColHeader>Remark</ColHeader></PipelineCell>
                <div style={{ width: 120, flexShrink: 0 }} />
              </div>
            </div>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-2)" }}>
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} style={{ background: "var(--color-surface-0)", border: "1px solid var(--color-surface-200)", borderRadius: "var(--radius-lg)", overflow: "hidden", display: "flex" }}>
                <div style={{ width: 4, background: "var(--color-surface-200)", flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0, padding: "var(--space-2)" }}>
                  <div style={{ display: "flex", alignItems: "stretch", flexWrap: "nowrap" }}>
                    <PipelineCell width={COLS.week} divider={false}><Skeleton width={110} height={16} /></PipelineCell>
                    <PipelineCell width={COLS.hours} align="center"><Skeleton width={36} height={36} borderRadius="50%" /></PipelineCell>
                    <PipelineCell width={COLS.status}><Skeleton width={74} height={20} borderRadius="var(--radius-full)" /></PipelineCell>
                    <PipelineCell width={COLS.approvedBy}><Skeleton width={70} height={14} /></PipelineCell>
                    <PipelineCell width={COLS.remark} grow><Skeleton width={90} height={14} /></PipelineCell>
                    <div style={{ width: 120, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "flex-end", padding: "0 0 0 var(--space-3)", borderLeft: "1px solid var(--color-surface-100)" }}>
                      <Skeleton width={62} height={22} borderRadius="var(--radius-full)" />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : !hasAnyRecords ? (
        <div style={{ background: "var(--color-surface-0)", border: "1px solid var(--color-surface-200)", borderRadius: "var(--radius-lg)", padding: "var(--space-12)", flex: 1, display: "flex", alignItems: "center", justifyContent: "center", textAlign: "center", color: "var(--color-surface-700)" }}>
          No record found.
        </div>
      ) : (
        <div style={{ background: "var(--color-surface-0)", border: "1px solid var(--color-surface-200)", borderRadius: "var(--radius-lg)", padding: "var(--space-4)", display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
          {/* The card fills the height the tab leaves it and the list scrolls
              inside, so as many rows as fit are visible. The column header sits
              inside the scroller as a sticky row, so it shares the rows' width
              (staying aligned with them) and stays put while they scroll. */}
          <div style={{ overflowY: "auto", flex: "1 1 auto", minHeight: 0 }}>
          <div style={{ display: "flex", flexWrap: "nowrap", position: "sticky", top: 0, zIndex: 1, background: "var(--color-surface-0)", paddingBottom: "var(--space-2)" }}>
            <div style={{ width: 4, flexShrink: 0 }} />
            <div style={{ flex: 1, minWidth: 0, padding: "var(--space-1) var(--space-2)" }}>
              <div style={{ display: "flex", flexWrap: "nowrap" }}>
                <PipelineCell width={COLS.week} divider={false}><ColHeader>Week</ColHeader></PipelineCell>
                <PipelineCell width={COLS.hours} align="center"><ColHeader>Hours</ColHeader></PipelineCell>
                <PipelineCell width={COLS.status}><ColHeader>Status</ColHeader></PipelineCell>
                <PipelineCell width={COLS.approvedBy}><ColHeader>Approved By</ColHeader></PipelineCell>
                <PipelineCell width={COLS.remark} grow><ColHeader>Remark</ColHeader></PipelineCell>
                <div style={{ width: 120, flexShrink: 0 }} />
              </div>
            </div>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-2)" }}>
            {entries.map((entry) => {
              if (!entry.record) {
                return (
                  <EmptyWeekRow
                    key={entry.weekStart}
                    weekStart={entry.weekStart}
                    state={emptyWeekState(entry.weekStart, entry.weekEnd, todayKey)}
                    selected={entry.weekStart === selectedWeekStart}
                    onSelect={() => onSelectWeek(entry.weekStart)}
                  />
                );
              }
              const row = entry.record;
              const { label: statusLabel, short: statusShort, fg: statusFg, bg: statusBg, barColor } = weeklyStatusDisplay(row.status, row.totalHours, row.expectedHours);
              const expanded = correctionEditor?.requestFor === row.weekStart;
              // A week still in progress has no settled total to dispute, so no
              // correction until it is over -- the API rejects it either way.
              const weekOver = row.weekEnd < todayKey;
              const extraHours = row.totalHours - row.expectedHours;
              // Share of the expected hours worked; no expectation (all leave or holidays) has no share to show.
              const pct = row.expectedHours > 0 ? (row.totalHours / row.expectedHours) * 100 : null;
              const selected = row.weekStart === selectedWeekStart;

              return (
                <div
                  key={row.weekStart}
                  onClick={() => onSelectWeek(row.weekStart)}
                  style={{
                    // Selection is app chrome, so it uses the brand blue rather
                    // than the purple --color-accent-* family, which marks the
                    // leave/personal category elsewhere. Mixed from the brand
                    // colour so the lift off the card reads the same in light
                    // and dark mode, where a fixed step would vanish.
                    background: selected ? "color-mix(in srgb, var(--color-brand-500) 12%, transparent)" : "var(--color-surface-0)",
                    border: selected ? "1px solid var(--color-brand-300)" : "1px solid var(--color-surface-200)",
                    borderRadius: "var(--radius-lg)", overflow: "hidden", display: "flex", cursor: "pointer",
                  }}
                >
                  <div style={{ width: 4, background: barColor, flexShrink: 0 }} />
                  <div style={{ flex: 1, minWidth: 0, padding: "var(--space-2)" }}>
                    {expanded && correctionEditor ? (
                      <div onClick={(e) => e.stopPropagation()} style={{ display: "flex", gap: "var(--space-3)", alignItems: "flex-end", flexWrap: "wrap" }}>
                        <div style={{ minWidth: 100 }}>
                          <div style={{ fontSize: 14, fontWeight: 700 }}>{isoWeekLabel(row.weekStart)}</div>
                        </div>
                        <label style={{ fontSize: 11, color: "var(--color-surface-700)" }}>
                          Corrected Total Hours
                          <input type="number" step="0.1" min="0" max="168" value={correctionEditor.hours} onChange={(e) => correctionEditor.onHoursChange(e.target.value)} style={{ display: "block", marginTop: 4, padding: "6px 8px", border: "1px solid var(--color-surface-200)", borderRadius: "var(--radius-md)", fontSize: 13, width: 120 }} />
                        </label>
                        <label style={{ fontSize: 11, color: "var(--color-surface-700)", flex: 1, minWidth: 220 }}>
                          Remark
                          <input type="text" value={correctionEditor.reason} onChange={(e) => correctionEditor.onReasonChange(e.target.value)} placeholder="Add a remark explaining this correction…" style={{ display: "block", width: "100%", boxSizing: "border-box", marginTop: 4, padding: "6px 8px", border: "1px solid var(--color-surface-200)", borderRadius: "var(--radius-md)", fontSize: 13 }} />
                        </label>
                        <AsyncButton size="sm" onClick={() => correctionEditor.onSubmit(row)} disabled={correctionEditor.busy}>
                          Save
                        </AsyncButton>
                        <button onClick={correctionEditor.onCancel} style={{ padding: "7px 14px", background: "transparent", color: "var(--color-surface-700)", border: "1px solid var(--color-surface-200)", borderRadius: "var(--radius-md)", fontSize: 12, cursor: "pointer" }}>
                          Cancel
                        </button>
                        {correctionEditor.err && <div style={{ width: "100%", color: "var(--color-error)", fontSize: 12 }}>{correctionEditor.err}</div>}
                      </div>
                    ) : (
                      <div style={{ display: "flex", alignItems: "stretch", flexWrap: "nowrap" }}>
                        <PipelineCell width={COLS.week} divider={false}>
                          {/* The label opens the week's days; stopPropagation keeps the
                              row's own click (select this week) working as before. */}
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); setExpandedWeek((w) => (w === row.weekStart ? null : row.weekStart)); }}
                            aria-expanded={expandedWeek === row.weekStart}
                            style={{ display: "flex", alignItems: "center", gap: 4, background: "none", border: "none", padding: 0, cursor: "pointer", fontSize: 14, fontWeight: 600, whiteSpace: "nowrap", color: "inherit" }}
                          >
                            <ChevronDown
                              size={14}
                              style={{ transition: "transform 0.2s ease", transform: expandedWeek === row.weekStart ? "rotate(180deg)" : "none", flexShrink: 0 }}
                            />
                            {isoWeekLabel(row.weekStart)}
                          </button>
                        </PipelineCell>

                        <PipelineCell width={COLS.hours} align="center">
                          <div className="flex items-center gap-1.5">
                            <ProgressRing percent={pct ?? 0} color={barColor} size={36}>
                              {pct == null ? "—" : `${Math.round(pct)}%`}
                            </ProgressRing>
                            <span className="whitespace-nowrap text-xs font-semibold">
                              {row.totalHours.toFixed(1)}
                              <span className="text-surface-700"> / {row.expectedHours.toFixed(1)}h</span>
                            </span>
                          </div>
                        </PipelineCell>

                        <PipelineCell width={COLS.status}>
                          {/* Badge and overtime sit on one line: stacking them made every
                              row taller than its content needed. Both carry the full text
                              on hover, so the short forms cost nothing. */}
                          <div style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0 }}>
                            <span title={statusLabel} style={{ background: statusBg, color: statusFg, padding: "3px 10px", borderRadius: "var(--radius-full)", fontSize: 12, fontWeight: 600, flexShrink: 0, whiteSpace: "nowrap" }}>{statusShort}</span>
                            {extraHours >= 0.1 && (
                              <span
                                title={`${extraHours.toFixed(1)} hours more than the ${row.expectedHours.toFixed(1)}h expected`}
                                style={{ fontSize: 11, fontWeight: 600, color: "var(--color-warning)", whiteSpace: "nowrap", cursor: "help" }}
                              >
                                +{extraHours.toFixed(1)}h
                              </span>
                            )}
                          </div>
                        </PipelineCell>

                        {/* Open week: the seven days take over Approved By + Remark on
                            the same line, so the row keeps the height it already had. */}
                        {expandedWeek === row.weekStart ? (
                          <PipelineCell width={COLS.approvedBy + COLS.remark} grow>
                            <WeekDayStrip days={row.days} />
                          </PipelineCell>
                        ) : (
                          <>
                            <PipelineCell width={COLS.approvedBy}>
                              {(row.status === "APPROVED" || row.status === "REJECTED") && row.approverRole ? (
                                <span title={approverRoleLabel(row.approverRole) ?? undefined} style={{ fontSize: 13, fontWeight: 500, display: "block", width: "100%", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{approverRoleLabel(row.approverRole)}</span>
                              ) : (
                                <span style={{ fontSize: 14, color: "var(--color-surface-700)" }}>—</span>
                              )}
                            </PipelineCell>

                            <PipelineCell width={COLS.remark} grow>
                              {row.approverRemark ? (
                                <span title={row.approverRemark} style={{ fontSize: 12, color: "var(--color-surface-700)", fontStyle: "italic", display: "block", width: "100%", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>"{row.approverRemark}"</span>
                              ) : (
                                <span style={{ fontSize: 14, color: "var(--color-surface-700)" }}>—</span>
                              )}
                            </PipelineCell>
                          </>
                        )}

                        {row.status !== "APPROVED" && (
                          <div style={{ width: 120, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 6, padding: "0 0 0 var(--space-3)", borderLeft: "1px solid var(--color-surface-100)" }}>
                            {row.correctionStatus === "PENDING" && (
                              <>
                                <span style={{ background: "var(--color-warning-bg)", color: "var(--color-warning-fg)", padding: "3px 10px", borderRadius: "var(--radius-full)", fontSize: 11, fontWeight: 600 }}>Pending</span>
                                {correctionEditor && (
                                  <Button variant="outline" size="xs" onClick={(e) => { e.stopPropagation(); correctionEditor.onEdit(row); }} title="Edit correction request">
                                    Edit
                                  </Button>
                                )}
                              </>
                            )}
                            {row.correctionStatus === "APPROVED" && <span style={{ background: "var(--color-success-bg)", color: "var(--color-success-fg)", padding: "3px 10px", borderRadius: "var(--radius-full)", fontSize: 11, fontWeight: 600 }}>Approved</span>}
                            {row.correctionStatus === "REJECTED" && <span style={{ background: "var(--color-error-bg)", color: "var(--color-error-fg)", padding: "3px 10px", borderRadius: "var(--radius-full)", fontSize: 11, fontWeight: 600 }}>Declined</span>}
                            {!row.correctionStatus && row.short && weekOver && correctionEditor && (
                              <Button variant="outline" size="xs" onClick={(e) => { e.stopPropagation(); correctionEditor.onRequest(row.weekStart); }} title="Request correction">
                                Correct
                              </Button>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          </div>
        </div>
      )}
    </div>
  );
}
